/**
 * WEB ordering – Terms & Privacy (VOP + GDPR) template, SOURCE OF TRUTH for the generated web text.
 *
 * Separate from the QR table-ordering template (qrTermsTemplate.js): the web shop has its own
 * fulfillment model (rozvoz / osobný odber), delivery zone + fee, packaging fee, alcohol notice and
 * pay-on-pickup option, so it needs its own variable schema and its own legal wording.
 *
 * Authored from `web-vop-gdpr.template.md` (template_version 1.0.0, vychádza z QR 1.1.0). The customer
 * fills the variables in AdminUI › zákazník › „Web nastavenia" › „Obchodné podmienky", clicks
 * „Vygenerovať", the text renders into a WYSIWYG editor (editable), and on save the final HTML is
 * shown to the guest in the web app modal.
 *
 * Syntax: Handlebars subset — {{var}}, {{{raw}}}, {{#if key}}…{{else}}…{{/if}}, {{#unless key}}…{{/unless}},
 * WITH nesting (a small recursive renderer below; the project has no handlebars dependency).
 *
 * Datavision (prevádzkovateľ systému) data and the notification provider (Brevo) are hardcoded here.
 * Language: Slovak only. Do not change the legal wording without a legal review.
 */

const WEB_TEMPLATE_VERSION = "1.0.0";

// Datavision Systems s.r.o. — hardcoded operator identity (shown in both documents).
export const WEB_DATAVISION = {
  obchodneMeno: "Datavision Systems s.r.o.",
  sidlo: "Slnečná ulica 1911/1, 922 03 Vrbové",
  ico: "56760817",
  zapisOr: "Obchodnom registri Okresného súdu Trnava, oddiel Sro, vložka č. 58599/T",
  email: "info@datavision.sk",
  telefon: "+421 918 755 096",
  emailPodpora: "info@datavision.sk",
  emailGdpr: "info@datavision.sk",
  // Notification provider for order confirmations / status updates.
  notifikaciePoskytovatel: "Brevo (Sendinblue SAS, Francúzsko – EÚ)",
};

/**
 * Customer-filled fields for the WEB shop terms. `required` gates the „Vygenerovať" button;
 * `requiredIf` is a key that must be truthy for the field to become required. `group` starts a visual
 * group. `prefill` is the key in the backend's `termsPrefill` map used to seed the field.
 *
 * Datavision globals (datavision.*) and system vars (platne_od, verzia, prevadzka_je_datavision) are
 * injected automatically in buildWebContext — they are NOT part of the form.
 */
export const WEB_TERMS_FIELDS = [
  { key: "prevadzka.obchodne_meno", group: "Údaje prevádzky", label: "Obchodné meno", type: "text", required: true, prefill: "prevadzka.obchodne_meno" },
  { key: "prevadzka.sidlo", label: "Sídlo", type: "text", required: true, prefill: "prevadzka.sidlo" },
  { key: "prevadzka.ico", label: "IČO", type: "text", required: true, prefill: "prevadzka.ico" },
  { key: "prevadzka.nazov_prevadzky", label: "Názov prevádzky", type: "text", required: true, prefill: "prevadzka.nazov_prevadzky" },
  { key: "prevadzka.adresa_prevadzky", label: "Adresa prevádzky", type: "text", required: true, prefill: "prevadzka.adresa_prevadzky" },
  { key: "prevadzka.email", label: "E-mail", type: "email", required: true, prefill: "prevadzka.email" },
  { key: "prevadzka.telefon", label: "Telefón", type: "text", required: true, prefill: "prevadzka.telefon" },
  { key: "prevadzka.zodpovedna_osoba", label: "Zodpovedná osoba (DPO) – nepovinné", type: "text", required: false, help: "Meno a kontakt zodpovednej osoby. Prázdne = nie je určená." },

  { key: "server.poskytovatel", group: "Server (hosting)", label: "Poskytovateľ servera", type: "text", required: true, help: "Poskytovateľ VPS vrátane sídla a krajiny dátového centra, napr. „XY s.r.o., Bratislava (dátové centrum SR)“." },

  { key: "dodanie.rozvoz", group: "Doručenie a odber", label: "Rozvoz", type: "bool", default: true },
  { key: "dodanie.osobny_odber", label: "Osobný odber", type: "bool", default: true },
  { key: "rozvoz.zona", label: "Rozvozová zóna", type: "text", requiredIf: "dodanie.rozvoz", help: "napr. Vrbové a okolie do 10 km" },
  { key: "rozvoz.poplatok", label: "Poplatok za rozvoz", type: "text", requiredIf: "dodanie.rozvoz", help: "napr. 2,50 €, nad 30 € zdarma" },
  { key: "rozvoz.min_objednavka", label: "Minimálna objednávka na rozvoz – nepovinné", type: "text", required: false, help: "napr. 15 €" },
  { key: "rozvoz.externy_kurier", label: "Externý kuriér – nepovinné", type: "text", required: false, help: "Názov externej kuriérskej služby. Prázdne = rozváža prevádzka sama." },

  { key: "platba.online", group: "Platba", label: "Platba online", type: "bool", default: true },
  { key: "platba.pri_prevzati", label: "Platba pri prevzatí", type: "bool", default: true, help: "Hotovosť alebo karta pri prevzatí (kuriérovi / v prevádzke)." },

  { key: "balne.zapnute", group: "Balné", label: "Účtujeme balné", type: "bool", default: false },
  { key: "balne.popis", label: "Popis balného", type: "text", requiredIf: "balne.zapnute", help: "napr. 0,30 € za každý obal" },

  { key: "alkohol", group: "Alkohol", label: "V ponuke sú alkoholické nápoje", type: "bool", default: false },

  { key: "gdpr.uchovavanie_objednavok_mesiace", group: "Uchovávanie údajov (GDPR)", label: "Objednávky (mesiace)", type: "number", default: 24, min: 1, required: true },
  { key: "gdpr.uchovavanie_logov_dni", label: "Technické logy (dni)", type: "number", default: 90, min: 1, required: true },

  { key: "doplnkove_podmienky", group: "Doplnkové podmienky prevádzky", label: "Doplnkové podmienky (bod 9 VOP) – nepovinné", type: "richtext", required: false },
];

/** Default variable values from the schema. */
export const webTermsDefaults = () => {
  const out = {};
  for (const f of WEB_TERMS_FIELDS) {
    if (f.type === "bool") out[f.key] = f.default === true;
    else if (f.type === "number") out[f.key] = f.default != null ? f.default : "";
    else out[f.key] = "";
  }
  return out;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const isRichEmpty = (v) => !v || v === "<p><br></p>" || String(v).replace(/<[^>]*>/g, "").trim() === "";

/**
 * Are all required inputs filled so the text can be generated? Also enforces: at least one payment
 * method and at least one fulfillment mode (the template assumes at least one of each).
 * @returns {{ ok: boolean, missing: string[] }}
 */
export const webTermsComplete = (vars) => {
  const v = vars || {};
  const missing = [];
  for (const f of WEB_TERMS_FIELDS) {
    const required = f.required === true || (f.requiredIf && v[f.requiredIf] === true);
    if (!required) continue;
    const val = v[f.key];
    if (f.type === "number") {
      if (val === "" || val == null || !Number.isFinite(Number(val)) || Number(val) < (f.min ?? 0)) missing.push(f.label);
    } else if (f.type === "email") {
      if (!val || !EMAIL_RE.test(String(val).trim())) missing.push(f.label);
    } else if (f.type === "richtext") {
      if (isRichEmpty(val)) missing.push(f.label);
    } else if (!String(val || "").trim()) {
      missing.push(f.label);
    }
  }
  if (!(v["platba.online"] === true || v["platba.pri_prevzati"] === true)) missing.push("aspoň jeden spôsob platby");
  if (!(v["dodanie.rozvoz"] === true || v["dodanie.osobny_odber"] === true)) missing.push("aspoň jeden spôsob doručenia");
  return { ok: missing.length === 0, missing };
};

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Today's date in Slovak format DD.MM.RRRR. */
const todaySk = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
};

/** A value is truthy for a template condition: boolean true, a non-empty/non-whitespace string, or a non-zero number. */
const isTruthy = (val) => {
  if (val === true) return true;
  if (typeof val === "number") return val !== 0;
  if (typeof val === "string") return val.trim() !== "";
  return false;
};

const substVars = (s, ctx) =>
  s
    .replace(/\{\{\{\s*([\w.]+)\s*\}\}\}/g, (_, k) => (ctx[k] != null ? String(ctx[k]) : ""))
    .replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, k) => escapeHtml(ctx[k] != null ? String(ctx[k]) : ""));

// Matches any block delimiter: {{#if x}}, {{#unless x}}, {{/if}}, {{/unless}}, {{else}}.
const BLOCK_RE = /\{\{#(if|unless)\s+([\w.]+)\}\}|\{\{\/(if|unless)\}\}|\{\{else\}\}/g;

/**
 * From just after an opening block tag, find the matching close (respecting nesting of any if/unless)
 * and return the inner text + the index just past the close tag.
 */
const extractBlock = (str, startIdx) => {
  BLOCK_RE.lastIndex = startIdx;
  let depth = 1;
  let m;
  while ((m = BLOCK_RE.exec(str))) {
    if (m[1]) depth += 1; // an opening {{#if}}/{{#unless}}
    else if (m[3]) {
      depth -= 1; // a closing {{/if}}/{{/unless}}
      if (depth === 0) return { inner: str.slice(startIdx, m.index), end: BLOCK_RE.lastIndex };
    }
    // {{else}} does not affect depth here.
  }
  // Unbalanced template — consume the rest.
  return { inner: str.slice(startIdx), end: str.length };
};

/** Split a block's inner text at its TOP-LEVEL {{else}} (ignoring nested blocks). */
const splitElse = (inner) => {
  BLOCK_RE.lastIndex = 0;
  let depth = 0;
  let m;
  while ((m = BLOCK_RE.exec(inner))) {
    if (m[1]) depth += 1;
    else if (m[3]) depth -= 1;
    else if (depth === 0) return [inner.slice(0, m.index), inner.slice(BLOCK_RE.lastIndex)];
  }
  return [inner, null];
};

/** Recursively render {{#if}}/{{#unless}}/{{else}} blocks and substitute variables. */
const applyTemplate = (str, ctx) => {
  let out = "";
  let pos = 0;
  const open = /\{\{#(if|unless)\s+([\w.]+)\}\}/g;
  for (;;) {
    open.lastIndex = pos;
    const m = open.exec(str);
    if (!m) {
      out += substVars(str.slice(pos), ctx);
      break;
    }
    out += substVars(str.slice(pos, m.index), ctx);
    const tag = m[1];
    const key = m[2];
    const { inner, end } = extractBlock(str, open.lastIndex);
    const [thenPart, elsePart] = splitElse(inner);
    const cond = tag === "if" ? isTruthy(ctx[key]) : !isTruthy(ctx[key]);
    out += applyTemplate(cond ? thenPart : elsePart || "", ctx);
    pos = end;
  }
  return out;
};

/** Build the flat render context: customer vars + injected Datavision globals + system vars. */
const buildWebContext = (vars) => {
  const v = vars || {};
  const ico = String(v["prevadzka.ico"] || "").replace(/\s/g, "").trim();
  const prevadzkaJeDatavision = ico !== "" && ico === WEB_DATAVISION.ico;
  return {
    ...v,
    // Datavision globals (operator identity) — always injected, never in the form.
    "datavision.obchodne_meno": WEB_DATAVISION.obchodneMeno,
    "datavision.sidlo": WEB_DATAVISION.sidlo,
    "datavision.ico": WEB_DATAVISION.ico,
    "datavision.zapis_or": WEB_DATAVISION.zapisOr,
    "datavision.email": WEB_DATAVISION.email,
    "datavision.telefon": WEB_DATAVISION.telefon,
    "datavision.email_podpora": WEB_DATAVISION.emailPodpora,
    "datavision.email_gdpr": WEB_DATAVISION.emailGdpr,
    // System vars.
    prevadzka_je_datavision: prevadzkaJeDatavision,
    platne_od: todaySk(),
    verzia: WEB_TEMPLATE_VERSION,
  };
};

// The legal template (SK). Datavision data + Brevo are literal; only customer variables are {{…}}.
// One combined document (VOP, then a separator, then Ochrana osobných údajov). Authored from
// web-vop-gdpr.template.md (markdown → HTML, Handlebars conditions kept verbatim).
const TEMPLATE_HTML = `
<h2>Obchodné podmienky web objednávania – {{prevadzka.nazov_prevadzky}}</h2>
<p>Platné od {{platne_od}} · verzia {{verzia}}.</p>

<h3>1. Kto je kto</h3>
<ul>
<li><strong>Predávajúci:</strong> {{prevadzka.obchodne_meno}}, so sídlom {{prevadzka.sidlo}}, IČO {{prevadzka.ico}}, prevádzka {{prevadzka.nazov_prevadzky}}, {{prevadzka.adresa_prevadzky}}, e-mail {{prevadzka.email}}, tel. {{prevadzka.telefon}}{{#if prevadzka_je_datavision}}, zapísaná v {{datavision.zapis_or}}{{/if}} (ďalej „predávajúci").</li>
{{#if prevadzka_je_datavision}}<li>Predávajúci zároveň prevádzkuje objednávkový systém.</li>{{else}}<li><strong>Prevádzkovateľ systému:</strong> {{datavision.obchodne_meno}}, so sídlom {{datavision.sidlo}}, IČO {{datavision.ico}}, zapísaná v {{datavision.zapis_or}}, e-mail {{datavision.email}}, tel. {{datavision.telefon}} (ďalej „Datavision").</li>
<li>Kúpnu zmluvu uzatvárate s predávajúcim. Datavision nie je zmluvnou stranou, len technicky zabezpečuje objednávku{{#if platba.online}} a online platbu{{/if}}. Za ponuku, ceny, kvalitu, prípravu a doručenie zodpovedá predávajúci, za funkčnosť systému Datavision.</li>{{/if}}
</ul>

<h3>2. Objednávka</h3>
<ol>
<li>Na stránke vyberiete položky, zvolíte {{#if dodanie.rozvoz}}{{#if dodanie.osobny_odber}}rozvoz alebo osobný odber{{else}}adresu doručenia{{/if}}{{else}}čas osobného odberu{{/if}}, vyplníte meno, telefón a e-mail{{#if dodanie.rozvoz}} (pri rozvoze aj adresu){{/if}} a zvolíte spôsob platby.</li>
<li>Pred odoslaním vidíte súhrn objednávky s konečnou cenou vrátane všetkých poplatkov.</li>
<li>Objednávku odošlete tlačidlom „Objednávka s povinnosťou platby".</li>
<li>Zmluva vzniká potvrdením objednávky predávajúcim; potvrdenie s predpokladaným časom {{#if dodanie.rozvoz}}doručenia{{else}}vyzdvihnutia{{/if}} dostanete e-mailom.</li>
<li>Objednávky prijímame počas otváracích hodín uvedených na stránke. Ak položka nie je dostupná, predávajúci vás bude kontaktovať alebo objednávku odmietne{{#if platba.online}}; zaplatenú sumu vráti rovnakým spôsobom platby do 14 dní{{/if}}.</li>
<li>Informácie o alergénoch sú uvedené pri položkách v menu; na požiadanie ich poskytne predávajúci.</li>
</ol>

<h3>3. Cena a platba</h3>
<ul>
<li>Ceny v menu sú konečné, vrátane DPH.{{#if balne.zapnute}} K cene sa pripočítava balné {{balne.popis}}.{{/if}}{{#if dodanie.rozvoz}} Poplatok za rozvoz: {{rozvoz.poplatok}}.{{#if rozvoz.min_objednavka}} Minimálna hodnota objednávky na rozvoz je {{rozvoz.min_objednavka}}.{{/if}}{{/if}} Všetky poplatky sú zobrazené v súhrne pred odoslaním objednávky.</li>
{{#if platba.online}}<li>Online platba (karta, Apple Pay, Google Pay) cez platobnú bránu Besteron (Besteron a.s.). Údaje o karte spracúva iba platobná brána.</li>{{/if}}
{{#if platba.pri_prevzati}}<li>Platba pri prevzatí v hotovosti alebo kartou{{#if dodanie.rozvoz}} kuriérovi{{#if dodanie.osobny_odber}} alebo v prevádzke{{/if}}{{else}} v prevádzke{{/if}}.</li>{{/if}}
<li>Predávajúci vám vydá pokladničný doklad.</li>
</ul>

<h3>4. Doručenie a odber</h3>
<ul>
{{#if dodanie.rozvoz}}<li><strong>Rozvoz:</strong> doručujeme v zóne {{rozvoz.zona}}{{#if rozvoz.externy_kurier}} prostredníctvom kuriérskej služby {{rozvoz.externy_kurier}}{{/if}}. Čas doručenia uvedený v potvrdení je orientačný. Ak vás kuriér na adrese nezastihne ani telefonicky, objednávku vráti do prevádzky a máte ju k dispozícii na osobný odber.</li>{{/if}}
{{#if dodanie.osobny_odber}}<li><strong>Osobný odber:</strong> objednávku si vyzdvihnete v prevádzke {{prevadzka.adresa_prevadzky}} v čase uvedenom v potvrdení.</li>{{/if}}
<li>Pokrmy sú pripravené na okamžitú konzumáciu. Ak objednávku neprevezmete, predávajúci ju nemusí uchovávať a má nárok na úhradu ceny.</li>
{{#if alkohol}}<li><strong>Alkohol:</strong> alkoholické nápoje predávame len osobám starším ako 18 rokov (zákon č. 219/1996 Z. z.). Pri prevzatí môžeme požadovať preukaz totožnosti; ak ho nepredložíte, alkohol nevydáme a jeho cenu vrátime.</li>{{/if}}
</ul>

<h3>5. Odstúpenie od zmluvy</h3>
<p>Od zmluvy nemôžete odstúpiť, lebo ide o pokrmy podliehajúce rýchlej skaze a o stravovacie služby poskytované v dohodnutom čase (§ 19 ods. 1 písm. d) a l) zákona č. 108/2024 Z. z.). Objednávku môžete zrušiť telefonicky u predávajúceho, kým sa nezačala pripravovať.</p>

<h3>6. Reklamácie</h3>
<ul>
<li>Vady jedla, nápojov alebo doručenia (chýbajúca alebo poškodená položka) reklamujte hneď pri prevzatí alebo bez zbytočného odkladu telefonicky na {{prevadzka.telefon}} či e-mailom na {{prevadzka.email}}, ideálne s fotografiou. Predávajúci zodpovedá za vady podľa Občianskeho zákonníka a reklamáciu vybaví bez zbytočného odkladu, najneskôr do 30 dní.</li>
<li>Technické problémy so stránkou alebo platbou hláste na {{datavision.email_podpora}}.</li>
</ul>

<h3>7. Riešenie sporov</h3>
<p>Ak nie ste spokojní s vybavením, požiadajte predávajúceho o nápravu. Ak odmietne alebo neodpovie do 30 dní, môžete podať návrh na alternatívne riešenie sporu Slovenskej obchodnej inšpekcii (www.soi.sk) alebo inému subjektu zo zoznamu Ministerstva hospodárstva SR (zákon č. 391/2015 Z. z.). Dozor vykonáva SOI.</p>

<h3>8. Záverečné ustanovenia</h3>
<p>Platí slovenské právo. Spracúvanie osobných údajov upravuje dokument Ochrana osobných údajov nižšie. Podmienky môžu byť zmenené; pre objednávku platí znenie účinné pri jej odoslaní.</p>
{{#if doplnkove_podmienky}}
<h3>9. Doplnkové podmienky prevádzky</h3>
{{{doplnkove_podmienky}}}
{{/if}}

<hr />

<h2>Ochrana osobných údajov – {{prevadzka.nazov_prevadzky}}</h2>
<p>Platné od {{platne_od}} · verzia {{verzia}}. Informácie podľa čl. 13 nariadenia (EÚ) 2016/679 (GDPR) a zákona č. 18/2018 Z. z.</p>

<h3>1. Kto spracúva vaše údaje</h3>
<ul>
<li><strong>Prevádzkovateľ:</strong> {{prevadzka.obchodne_meno}}, {{prevadzka.sidlo}}, IČO {{prevadzka.ico}}, e-mail {{prevadzka.email}}. Rozhoduje o účele spracúvania a je kontaktom pre vaše práva.</li>
{{#unless prevadzka_je_datavision}}<li><strong>Sprostredkovateľ:</strong> {{datavision.obchodne_meno}}, {{datavision.sidlo}}, IČO {{datavision.ico}}, e-mail {{datavision.email_gdpr}}, ktorá prevádzkuje objednávkovú stránku na svojom serveri na základe zmluvy s prevádzkou (čl. 28 GDPR). Pre technické logy a bezpečnosť systému je Datavision samostatným prevádzkovateľom.</li>{{/unless}}
<li>Zodpovedná osoba: {{#if prevadzka.zodpovedna_osoba}}{{prevadzka.zodpovedna_osoba}}{{else}}nie je určená{{/if}}.</li>
</ul>

<h3>2. Aké údaje, na čo a na akom základe</h3>
<table>
<thead><tr><th>Údaje</th><th>Účel</th><th>Právny základ</th><th>Doba uchovávania</th></tr></thead>
<tbody>
<tr><td>Meno, telefón, e-mail{{#if dodanie.rozvoz}}, adresa doručenia, poznámka pre kuriéra{{/if}}, objednané položky, cena</td><td>Prijatie, príprava{{#if dodanie.rozvoz}}, doručenie{{/if}} a vydanie objednávky, oznamy o jej stave, reklamácie</td><td>Plnenie zmluvy, čl. 6 ods. 1 písm. b) GDPR</td><td>{{gdpr.uchovavanie_objednavok_mesiace}} mesiacov od objednávky</td></tr>
<tr><td>Doklad o platbe (bez údajov o karte)</td><td>Účtovníctvo a dane</td><td>Zákonná povinnosť, čl. 6 ods. 1 písm. c) GDPR, zákon č. 431/2002 Z. z.</td><td>10 rokov</td></tr>
<tr><td>IP adresa, technické logy</td><td>Bezpečnosť a predchádzanie zneužitiu</td><td>Oprávnený záujem na bezpečnej prevádzke stránky, čl. 6 ods. 1 písm. f) GDPR</td><td>{{gdpr.uchovavanie_logov_dni}} dní</td></tr>
</tbody>
</table>
<p>Meno a kontakt{{#if dodanie.rozvoz}} (pri rozvoze aj adresa){{/if}} sú potrebné na uzavretie a splnenie zmluvy; bez nich objednávku nemožno odoslať.</p>

<h3>3. Príjemcovia</h3>
<ul>
<li><strong>{{server.poskytovatel}}</strong> – prenájom servera, na ktorom beží objednávková stránka a databáza; údaje sú uložené v EÚ.</li>
{{#if platba.online}}<li><strong>Besteron a.s.</strong>, Teslova 20a, 821 02 Bratislava, IČO 47 866 233 – platobná brána. Údaje o platbe a karte zadávate priamo jej a spracúva ich ako samostatný prevádzkovateľ podľa svojich zásad ochrany osobných údajov (besteron.com/privacy-policy). Predávajúci ani Datavision údaje o karte nevidia.</li>{{/if}}
<li><strong>Brevo (Sendinblue SAS, Francúzsko – EÚ)</strong> – odosielanie potvrdení a oznamov o stave objednávky.</li>
{{#if rozvoz.externy_kurier}}<li><strong>{{rozvoz.externy_kurier}}</strong> – doručenie objednávky (meno, telefón, adresa, poznámka).</li>{{/if}}
<li>Orgány verejnej moci, ak to vyžaduje zákon.</li>
</ul>
<p>Údaje neprenášame mimo EÚ/EHP. Automatizované rozhodovanie ani profilovanie nepoužívame.</p>

<h3>4. Cookies</h3>
<p>Stránka používa len nevyhnutné cookies (košík, relácia, jazyk), ktoré nevyžadujú súhlas. Analytické ani marketingové cookies nepoužívame.</p>

<h3>5. Vaše práva</h3>
<p>Máte právo na prístup, opravu, vymazanie, obmedzenie spracúvania, prenosnosť údajov a námietku proti spracúvaniu na základe oprávneného záujmu. Žiadosť pošlite na {{prevadzka.email}}{{#unless prevadzka_je_datavision}} alebo {{datavision.email_gdpr}}{{/unless}}; vybavíme ju do 1 mesiaca. Sťažnosť môžete podať Úradu na ochranu osobných údajov SR, Hraničná 12, 820 07 Bratislava (dataprotection.gov.sk).</p>
`.trim();

/** Render the final HTML from the customer variables. */
export const renderWebTerms = (vars) => applyTemplate(TEMPLATE_HTML, buildWebContext(vars));
